import React from 'react';
import {
    Box, Typography, CardContent, Grid, Chip, Avatar,
    Stack, Divider, Paper, TextField, Button
} from '@mui/material';
import {
    Route as RouteIcon,
    DirectionsCar as DirectionsCarIcon,
    CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import { tripDateView, tripDurationFormat, tripHoursFormat } from '../../../utils/dateFormat';
import moment from 'moment';

const RouteRevisionModal = ({
    trip,
    formValues,
    errors,
    onFormChange,
    onSubmit,
    submitting
}) => {
    const CompactCard = ({ title, icon, children, color = '#667eea' }) => (
        <Paper
            variant="outlined"
            sx={{
                mb: 2,
                borderRadius: 2,
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Avatar
                        sx={{
                            bgcolor: color,
                            width: 28,
                            height: 28,
                            mr: 1.25
                        }}
                    >
                        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
                    </Avatar>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: color }}>
                        {title}
                    </Typography>
                </Box>
                {children}
            </CardContent>
        </Paper>
    );

    const DataRow = ({ label, value, variant = 'standard' }) => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                py: 0.5,
                minHeight: '20px'
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    flex: 1,
                    mr: 1
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: variant === 'bold' ? 700 : 600,
                    color: variant === 'bold' ? 'primary.main' : 'text.primary',
                    lineHeight: 1.3,
                    textAlign: 'right',
                    flex: 1
                }}
            >
                {value}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ width: '100%', height: '100%', p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Route Revision Request
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Provide the revision details before updating the route path.
                </Typography>
                <Box component="form" onSubmit={onSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                size="small"
                                label="Revised By"
                                placeholder="Name of the person performing this revision / enter your name"
                                value={formValues?.name || ''}
                                onChange={(event) => onFormChange?.('name', event.target.value)}
                                error={Boolean(errors?.name)}
                                helperText={errors?.name || 'Name of the person performing this revision / enter your name'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                size="small"
                                label="Reason"
                                placeholder="Why are you revising this route?"
                                value={formValues?.reason || ''}
                                onChange={(event) => onFormChange?.('reason', event.target.value)}
                                error={Boolean(errors?.reason)}
                                helperText={errors?.reason || 'Why are you revising this route?'}
                                multiline
                                minRows={3}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={submitting}
                            >
                                Submit Revision Request
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {/* Route Details */}
            <CompactCard
                title="Planned Route"
                icon={<RouteIcon />}
                color="#667eea"
            >
                <Stack spacing={0.25}>
                    <DataRow
                        label="Route Name"
                        value={trip?.route?.routeName?.split('|')[0] || 'N/A'}
                        variant="bold"
                    />
                    <DataRow
                        label="Route Type"
                        value={trip?.route?.routeType?.split(/(?=[A-Z])/).join(' ').toLowerCase() || 'N/A'}
                    />
                    <DataRow
                        label="Planned Distance"
                        value={`${trip?.route?.routeLength?.toFixed(1) || 0} km`}
                    />
                    <DataRow
                        label="Duration"
                        value={tripDurationFormat(trip?.route?.routeDuration, 's') || 'N/A'}
                    />

                    {trip?.route?.viaLocations?.length > 0 && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, mb: 0.5 }}>
                                Stops ({trip.route.viaLocations.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {trip.route.viaLocations.map((location, index) => (
                                    <Chip
                                        key={index}
                                        label={location.locationName}
                                        size="small"
                                        sx={{
                                            bgcolor: 'primary.light',
                                            color: 'primary.main',
                                            fontWeight: 600
                                        }}
                                    />
                                ))}
                            </Box>
                        </>
                    )}

                    <DataRow
                        label="Route Points"
                        value={trip?.route?.routePath?.length || 0}
                    />
                    <DataRow
                        label="Points/km"
                        value={`${(trip?.route?.routePath?.length / trip?.route?.routeLength)?.toFixed(1) || 0}`}
                    />
                </Stack>
            </CompactCard>

            {/* Actual Performance */}
            <CompactCard
                title="Actual Performance"
                icon={<DirectionsCarIcon />}
                color="#4caf50"
            >
                <Stack spacing={0.25}>
                    <DataRow
                        label="Distance"
                        value={`${trip?.truckRunDistance?.toFixed(1) || 0} km`}
                        variant="bold"
                    />
                    <DataRow
                        label="Start Time"
                        value={tripDateView(trip?.actualStartTime) || 'N/A'}
                    />
                    <DataRow
                        label="Drive Time"
                        value={tripHoursFormat(trip?.runDuration) || 'N/A'}
                    />
                    <DataRow
                        label="Stop Time"
                        value={tripHoursFormat(trip?.parkedDuration) || 'N/A'}
                    />
                    <DataRow
                        label="Trip Duration"
                        value={tripDurationFormat(moment.duration(moment.utc(trip?.actualEndTime).diff(moment.utc(trip?.actualStartTime))).asMinutes()) || 'N/A'}
                    />
                    <DataRow
                        label="End Time"
                        value={tripDateView(trip?.actualEndTime) || 'N/A'}
                    />
                    <DataRow
                        label="Top Speed"
                        value={`${trip?.topSpeed?.toFixed(1) || 0} km/h`}
                    />
                    <DataRow
                        label="Avg Speed"
                        value={`${trip?.averageSpeed?.toFixed(1) || 0} km/h`}
                    />
                    <DataRow
                        label="Stops"
                        value={trip?.stops?.length || 0}
                    />
                    <DataRow
                        label="Trip Points"
                        value={trip?.tripPath?.length || 0}
                    />
                    <DataRow
                        label="Points/km"
                        value={`${(trip?.tripPath?.length / trip?.truckRunDistance)?.toFixed(1) || 0}`}
                    />
                </Stack>
            </CompactCard>

            {/* Comparison Analysis */}
            <CompactCard
                title="Comparison"
                icon={<CompareArrowsIcon />}
                color="#ff9800"
            >
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.main', display: 'block', mb: 0.5 }}>
                        Distance Analysis
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                {trip?.route?.routeLength?.toFixed(1) || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Planned
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'warning.main' }}>
                            vs
                        </Typography>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
                                {trip?.truckRunDistance?.toFixed(1) || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Actual
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.main', display: 'block', mb: 0.5 }}>
                        Route Points
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                {trip?.route?.routePath?.length || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Route
                            </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: 'warning.main' }}>
                            vs
                        </Typography>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
                                {trip?.tripPath?.length || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Trip
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </CompactCard>
        </Box>
    );
};

export default RouteRevisionModal;
